import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaUniversity, FaMobileAlt, FaShieldAlt, FaChartLine, FaUserTie, FaGlobe,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaCreditCard,
    FaCoins, FaFingerprint, FaNetworkWired, FaMoneyBillWave, FaLaptopCode
} from 'react-icons/fa';
import heroImage from '../../assets/Online Payment & Digital Finance _ Credit Card Transaction and Investment Data Visualization.png';

const BankingPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaUniversity, title: 'Core Banking Modernization', desc: 'Migrate legacy mainframes to cloud-native architectures.', image: 'https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?w=800&q=80' },
        { icon: FaMobileAlt, title: 'Digital Wallets & Payments', desc: 'Secure, frictionless mobile payment solutions.', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?w=800&q=80' },
        { icon: FaShieldAlt, title: 'Fraud Detection AI', desc: 'Real-time behavioral analysis to stop financial crime.', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80' },
        { icon: FaUserTie, title: 'Wealth Management', desc: 'Robo-advisors and personalized portfolio tools.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80' },
        { icon: FaNetworkWired, title: 'Open Banking APIs', desc: 'Secure data sharing for fintech integration.', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80' },
        { icon: FaCoins, title: 'Blockchain Settlements', desc: 'Instant cross-border payments using DLT.', image: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=800&q=80' }
    ];

    const platforms = [
        { name: 'Temenos', icon: FaUniversity, desc: 'Core Banking' },
        { name: 'Mambu', icon: FaCloud, desc: 'SaaS Banking' },
        { name: 'Finacle', icon: FaNetworkWired, desc: 'Universal Banking' },
        { name: 'Plaid', icon: FaLink, desc: 'API Connectivity' },
        { name: 'Stripe', icon: FaCreditCard, desc: 'Payments' },
        { name: 'AWS FinServices', icon: FaServer, desc: 'Cloud Infra' }
    ];

    // Icons for platforms
    function FaCloud(props) { return <FaGlobe {...props} /> }
    function FaLink(props) { return <FaNetworkWired {...props} /> }
    function FaServer(props) { return <FaDatabase {...props} /> }
    function FaDatabase(props) { return <FaNetworkWired {...props} /> } // Fallback

    // Horizontal Flow Steps (Digital Transformation)
    const flowSteps = [
        { title: 'Assess', icon: FaSearch, color: 'from-blue-500 to-indigo-500' },
        { title: 'Architect', icon: FaPencilRuler, color: 'from-cyan-500 to-blue-500' },
        { title: 'Migrate', icon: FaCloudUploadAlt, color: 'from-indigo-500 to-purple-500' },
        { title: 'Secure', icon: FaShieldAlt, color: 'from-red-500 to-pink-500' },
        { title: 'Launch', icon: FaRocket, color: 'from-green-500 to-emerald-500' },
        { title: 'Scale', icon: FaChartLine, color: 'from-orange-500 to-yellow-500' }
    ];

    // Missing icons fallback
    function FaSearch(props) { return <FaChartLine {...props} /> }
    function FaPencilRuler(props) { return <FaLaptopCode {...props} /> }
    function FaCloudUploadAlt(props) { return <FaGlobe {...props} /> }
    function FaRocket(props) { return <FaMobileAlt {...props} /> }


    const faqs = [
        { q: 'How do you handle legacy migration?', a: 'We use a "strangler fig" pattern to gradually replace legacy modules with microservices, ensuring zero downtime.' },
        { q: 'Is your software compliant?', a: 'Yes, all our solutions are PCI-DSS, GDPR, and ISO 27001 compliant tailored to local banking regulations.' },
        { q: 'Can you integrate with our existing core?', a: 'Absolutely. We specialize in building API layers that sit on top of legacy cores (like Mainframes) to enable modern digital channels.' },
        { q: 'What about security?', a: 'Security is "Day 0". We implement bank-grade encryption (AES-256), MFA, and continuous penetration testing.' },
        { q: 'Do you build mobile banking apps?', a: 'Yes, we build native (iOS/Android) and cross-platform (Flutter/React Native) apps with biometrics and smooth UX.' }
    ];

    const whyChooseUs = [
        { title: 'Domain Expertise', desc: 'We understand Basel III, KYC/AML, and liquidity ratios.' },
        { title: 'Speed to Market', desc: 'Launch new financial products in weeks, not years.' },
        { title: 'Secure by Design', desc: 'Embed security into every line of code we write.' },
        { title: 'Data-Driven', desc: 'Unlock the value of your transaction data for personalization.' },
        { title: '24/7 Support', desc: 'Mission-critical support for always-on financial systems.' },
        { title: 'UX Focused', desc: 'We make banking interface as simple as a social media app.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-blue-300 mb-4">Industries &gt; Financial Services</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Banking & <span className="text-blue-400 font-normal">Payments</span></h1>
                        <p className="text-lg font-light text-blue-100 leading-relaxed mb-8 max-w-2xl">
                            Future-proof your institution with cloud-native engines and seamless digital experiences that customers love.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-blue-600 text-white font-medium rounded-sm hover:bg-blue-700 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Consult An Architect
                            </a>
                            <a href="#services" className="px-8 py-3 border border-blue-300 text-white font-medium rounded-sm hover:bg-white hover:text-blue-900 transition-all text-sm uppercase tracking-wide">
                                View Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">The Digital Mandate</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Banks must evolve into technology companies.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Transactions / Sec', val: '50k+' },
                            { label: 'Uptime SLA', val: '99.999%' },
                            { label: 'Fraud Stopped', val: '$100M+' },
                            { label: 'Accounts Managed', val: '5M+' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-blue-600 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Banking Solutions</h2>
                        <p className="text-lg text-slate-600">
                            End-to-end capabilities from the core to the customer.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-black/20 to-transparent"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-blue-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-blue-100 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-blue-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Integration Partners</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {platforms.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-blue-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Transformation Roadmap</h2>
                        <p className="text-slate-600">A risk-mitigated approach to modernization.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-blue-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-blue-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Trust <br />Engineered</h2>
                            <p className="text-blue-100 text-lg leading-relaxed mb-8">
                                Whether building a Neo-bank from scratch or upgrading a Tier-1 incumbent, we bring the engineering rigour required for money.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-blue-400 font-medium hover:text-white transition-colors">
                                Schedule Discovery Call <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-blue-800/50 p-6 rounded-sm border border-blue-700 hover:border-cyan-500/50 transition-colors">
                                    <FaCheckCircle className="text-cyan-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-blue-200 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Banking FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-blue-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-blue-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Modernize Finance</h2>
                            <p className="text-slate-500 mb-8">Let's build the bank of tomorrow.</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="Jane Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Institution</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="Bank Corp" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="jane@bank.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Core Need</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors text-slate-700">
                                        <option>Modernization</option>
                                        <option>App Development</option>
                                        <option>Security Audit</option>
                                        <option>Blockchain</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-blue-600 text-white font-bold py-4 rounded-sm hover:bg-blue-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Begin Transformation
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BankingPage;
